import { noop, Observable, of, Subject } from 'rxjs';
import { IdAndLabel } from '../model/id-and-label';
import { catchError, map, tap } from 'rxjs/operators';
import { ChecklistNode } from '../../buildings/checklists/checklist/model/checklist-node';
import { ChecklistNodeCheckpoint, Checkpoint } from '../../buildings/checklists/checklist/model/checklist-node-checkpoint';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiRouteConfig } from '../../../config/api.route.config';
import { Injectable } from '@angular/core';
import { Checklist } from '../model/checklist';
import { TemplateCheckpoint } from '../model/template-checkpoint';
import { TemplateChecklistNodeType } from '../enums/enums';
import { AddChecklistRequest } from '../model/add-checklist-request';
import {flattenChecklistNode} from "../../buildings/checklists/checklist/checklist.component";
import {sortBy} from "../../../helpers/sorting";

@Injectable()
export class ChecklistsService {

  public updated$: Subject<any> = new Subject<any>();
  public checkpointsUpdated$: Subject<any> = new Subject<any>();
  public checklistUpdated$: Subject<any> = new Subject<any>();
  public added$: Subject<any> = new Subject<any>();

  constructor(private httpClient: HttpClient, private api: ApiRouteConfig) { }


  public async predefinedCheckpoints(buildingId: number) {
    const checklists = await this.getChecklists(null, `${buildingId}`, true).toPromise();
    const predefinedChecklists = checklists.filter(({businessId}) => !businessId);
    const fullPredefinedChecklists = await Promise.all(
      predefinedChecklists.map(checklist => this.getChecklist(checklist.id).toPromise()),
    );

    const predefinedCheckpoints: ChecklistNodeCheckpoint[] = fullPredefinedChecklists.reduce((checkpoints, checklist) => {
      const checklistCheckpoints: ChecklistNodeCheckpoint[] = flattenChecklistNode(checklist).reduce((nodeCheckpoints, node) => ([
          ...nodeCheckpoints,
          ...node.checkPoints,
      ]), []);
      return ([
        ...checkpoints,
        ...checklistCheckpoints,
      ]);
    }, [] as ChecklistNodeCheckpoint[]);

    return predefinedCheckpoints;
  }

  public getChecklists(httpParams?: HttpParams, buildingId: string = null, includePredefined: boolean = false): Observable<Checklist[]> {
    const url = this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLISTS, [buildingId]);
    let params = null;
    if(httpParams){
      params = httpParams.append('includePredefined', includePredefined.toString()).append('RootStructureId', buildingId?.toString());
    }else {
      params = new HttpParams().set('includePredefined', includePredefined.toString()).set('RootStructureId', buildingId?.toString());
    }

    return this.httpClient.get<Checklist[]>(url, { params })
      .pipe(
        catchError(() => of([])),
      );
  }

  public getAvailableChecklistTypes(): Observable<TemplateChecklistNodeType[]> {
    return this.httpClient.get<TemplateChecklistNodeType[]>(this.api.BUILDING_CHECKLIST_TYPES);
  }

  public getChecklist(id: number): Observable<ChecklistNode> {
    return this.httpClient.get<ChecklistNode>(this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLIST, [id.toString()]))
      .pipe(
        catchError(error => of({} as ChecklistNode)),
      );
  }

  public getCheckpoints(): Observable<IdAndLabel[]> {
    return this.httpClient.get<any>(this.api.BUILDING_CHECKLIST_CHECKPOINTS)
      .pipe(
        map(r => r.filter(checkpoint => checkpoint.active && checkpoint.label != null)),
        catchError(error => of([]))
      );
  }

  public addNewCheckpoint(newCheckpoint: TemplateCheckpoint): Observable<TemplateCheckpoint> {
    return this.httpClient.post<TemplateCheckpoint>(this.api.BUILDING_CHECKLIST_CHECKPOINTS, newCheckpoint)
      .pipe(
        tap(() => this.checkpointsUpdated$.next()),
        catchError(error => of({} as TemplateCheckpoint)),
      );
  }

  public duplicateNewCheckpoint(checkpoint: TemplateCheckpoint|ChecklistNodeCheckpoint): Observable<TemplateCheckpoint> {
    return this.httpClient.post<TemplateCheckpoint>(this.api.BUILDING_CHECKLIST_CHECKPOINTS_DUPLICATE, checkpoint)
      .pipe(
        tap(() => this.checkpointsUpdated$.next()),
        catchError(error => of({} as TemplateCheckpoint)),
      );
  }

  public updateCheckpoint(checkPoint: Checkpoint){
    return this.httpClient.put<TemplateCheckpoint>(this.api.BUILDING_CHECKLIST_CHECKPOINTS, checkPoint)
      .pipe(
        tap(() => this.checkpointsUpdated$.next()),
        catchError(error => of({} as TemplateCheckpoint)),
      );
  }

  public addChecklist(request: AddChecklistRequest): Observable<ChecklistNode> {
    return this.httpClient.post(this.api.BUILDING_CHECKLISTS_NODE_ADD, request)
      .pipe(
        tap((c) => this.added$.next(c)),
        catchError(() => of(null)));
  }

  public addNode(buildingId: number, parentNodeId: number, label: string): Observable<any> {
    const node = { label: label, parentId: parentNodeId, rootStructureId: buildingId } as ChecklistNode;
    return this.httpClient.post<ChecklistNode>(this.api.BUILDING_CHECKLISTS_NODE_ADD, node).pipe(
      tap(() => this.checklistUpdated$.next(null)),
      catchError(() => of(null)),
    );
  }

  public addCheckpoint(templateChecklistNodeId: number, templateCheckPointId: number): Observable<any> {
    const checkpointNode = { templateChecklistNodeId: templateChecklistNodeId, templateCheckPointId: templateCheckPointId, mandatory: false } as ChecklistNodeCheckpoint;
    return this.httpClient.post(this.api.BUILDING_CHECKLISTS_CHECKPOINT_ADD, checkpointNode).pipe(
      tap(() => this.checklistUpdated$.next(null)),
      catchError(() => of(null)),
    );
  }

  public updateNode(node: ChecklistNode, isRoot: boolean): Observable<any> {
    return this.httpClient.put(this.api.BUILDING_CHECKLISTS_NODE_UPDATE, node).pipe(
      tap(() => isRoot ? this.updated$.next(null) : noop()),
      catchError(() => of(null)),
    );
  }

  public updateChecklistNodeCheckpoint(node: ChecklistNodeCheckpoint): Observable<any> {
    return this.httpClient.put(this.api.BUILDING_CHECKLISTS_CHECKPOINT_UPDATE, node).pipe(
      catchError(() => of(null)),
    );
  }

  public deleteNode(id: number): Observable<any> {
    return this.httpClient.delete<number>(this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLISTS_NODE_DELETE, [id.toString()]))
      .pipe(
        tap((nodeId) => this.checklistUpdated$.next(nodeId)),
        catchError(() => of(true))
      );
  }

  public deleteNodeCheckpoint(id: number): Observable<any> {
    return this.httpClient.delete(this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLISTS_CHECKPOINT_DELETE, [id.toString()]))
      .pipe(
        tap(() => this.checklistUpdated$.next(null)),
        catchError(() => of(true))
      );
  }

  public duplicateNode(sourceChecklistNodeId: number, destinationParentChecklistNodeId): Observable<any> {
    const url = this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLISTS_NODE_DUPLICATE, [sourceChecklistNodeId.toString()]);
    return this.httpClient.post<ChecklistNode>(url, destinationParentChecklistNodeId)
      .pipe(
        catchError(() => of(true))
      );
  }

  public duplicateChecklist(sourceChecklistNodeId: number, rootStructureId: number[]): Observable<ChecklistNode[]> {
    const url = this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLISTS_DUPLICATE, [sourceChecklistNodeId.toString()]);
    return this.httpClient.post<ChecklistNode>(url, rootStructureId)
      .pipe(
        tap((nodes) => {
          var c = nodes[0];
          this.updated$.next();
          this.added$.next(c);
        }),
        catchError(() => of(null))
      );
  }

  public sortCheckpoints(parentNodeId: number, sortedCheckpointsIds: number[]): Observable<any> {
    const params = new HttpParams().set('checklistNodeId', parentNodeId.toString());
    return this.httpClient.post(this.api.BUILDING_CHECKLISTS_CHECKPOINTS_SORT, sortedCheckpointsIds, { params })
      .pipe(
        catchError(() => of(true))
      );
  }

  public sortNodes(parentNodeId: number, sortedNodesIds: number[]): Observable<any> {
    const url = this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLISTS_NODES_SORT, [parentNodeId.toString()]);
    return this.httpClient.post(url, sortedNodesIds)
      .pipe(
        catchError(() => of(true))
      );
  }

  public toggleVisibility(rootStructureId: string, templateChecklistRootNodeId: number, visible: boolean) {
    const payload = {templateChecklistRootNodeId, visible};
    const endpoint = this.api.getRouteWithInjectedParams(this.api.BUILDING_CHECKLIST_VISIBILITY, [`${rootStructureId}`]);
    return this.httpClient.post(endpoint, payload);
  }


}

